import React, { useState, useRef, useEffect } from 'react';
import { Modal, Table, Tag, Button, Spin, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { TimezoneUtils } from '../../../../utils/timezoneUtils';
import { getEFormApprovalLabels } from '../../../../utils/eformApprovalLabels';

const DrillDownModal = ({ open, onClose, title, instanceIds, dataSource }) => {
  const { t } = useLanguage();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewIframeSrc, setPreviewIframeSrc] = useState('');
  const iframeRef = useRef(null);

  const records = (dataSource || []).filter((r) => (instanceIds || []).includes(r.id));

  const fetchInstanceHtml = async (id) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/eforminstances/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const detail = await response.json();
    return detail.htmlCode || detail.filledHtmlCode || '';
  };

  const buildIframeSrc = (html) => {
    if (!html) return '';
    const baseUrl = window.location.origin;
    const processed = html.replace(
      /<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gi,
      (match, before, src, after) => {
        if (src && src.startsWith('/') && !src.startsWith('//') && !/^https?:\/\//i.test(src)) {
          return `<img${before} src="${baseUrl}${src}"${after}>`;
        }
        return match;
      }
    );
    const fullHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{margin:0;padding:20px;font-family:Arial,sans-serif;background:#fff}*{box-sizing:border-box}table{width:100%!important;border-collapse:collapse}img{max-width:100%!important;height:auto!important}</style></head><body>${processed}</body></html>`;
    return URL.createObjectURL(new Blob([fullHtml], { type: 'text/html' }));
  };

  const handlePreview = async (record) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const html = await fetchInstanceHtml(record.id);
      setPreviewIframeSrc(buildIframeSrc(html));
    } catch {
      message.error(t('reports.loadDataFailed') || '載入預覽失敗');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    if (previewIframeSrc) URL.revokeObjectURL(previewIframeSrc);
    setPreviewIframeSrc('');
    setPreviewOpen(false);
  };

  useEffect(() => {
    return () => {
      if (previewIframeSrc) URL.revokeObjectURL(previewIframeSrc);
    };
  }, [previewIframeSrc]);

  const columns = [
    {
      title: t('common.formName') || '表單名稱',
      dataIndex: 'formName',
      key: 'formName',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('common.instanceName') || '實例名稱',
      dataIndex: 'instanceName',
      key: 'instanceName',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('common.status') || '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => {
        const colorMap = { Pending: 'orange', Approved: 'green', Rejected: 'red', Submitted: 'blue' };
        const approvalLabels = getEFormApprovalLabels(record, t, {
          approve: 'common.approved',
          reject: 'common.rejected'
        });
        const displayText = status === 'Approved'
          ? approvalLabels.approve
          : status === 'Rejected'
            ? approvalLabels.reject
            : status === 'Pending'
              ? (t('common.pending') || '待處理')
              : status === 'Submitted'
                ? (t('common.submitted') || '已提交')
                : (status || '-');
        return <Tag color={colorMap[status] || 'default'}>{displayText}</Tag>;
      },
    },
    {
      title: t('common.createdAt') || '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (v) => (v ? TimezoneUtils.formatDateTime(v, 'YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: t('common.action') || '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button icon={<EyeOutlined />} size="small" onClick={() => handlePreview(record)}>
          {t('reports.eformInstancesPreview') || '預覽'}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={title || (t('reports.pivotDrillDownTitle') || '明細記錄')}
        open={open}
        onCancel={onClose}
        footer={null}
        width="80vw"
        style={{ top: 40 }}
        destroyOnClose
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 800 }}
          size="small"
        />
      </Modal>
      <Modal
        title={t('reports.eformInstancesPreview') || '預覽'}
        open={previewOpen}
        onCancel={handlePreviewClose}
        footer={<Button onClick={handlePreviewClose}>{t('common.close') || '關閉'}</Button>}
        width="85vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        <Spin spinning={previewLoading}>
          {previewIframeSrc ? (
            <iframe
              ref={iframeRef}
              src={previewIframeSrc}
              style={{ width: '100%', height: '70vh', border: '1px solid #d9d9d9', borderRadius: 8, background: '#fff' }}
              title="EForm Preview"
            />
          ) : (
            !previewLoading && (
              <div style={{ padding: 40, textAlign: 'center' }}>
                {t('reports.eformInstancesNoPreview') || '沒有可預覽的內容'}
              </div>
            )
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default DrillDownModal;
